import { useMemo, useState } from 'react'
import { MotionConfig } from 'motion/react'
import { Skeleton } from '@/components/ui/skeleton'
import { useDashboardChannel, type Discipline } from '@/hooks/useDashboardChannel'
import { workoutNouns } from '@/lib/discipline'
import { DisciplineDock } from '@/components/dashboard/DisciplineDock'
import { Hero } from '@/components/dashboard/Hero'
import { LiveFeed } from '@/components/dashboard/LiveFeed'
import { RankedList } from '@/components/dashboard/RankedList'
import { SoundMix } from '@/components/dashboard/SoundMix'
import { StatBento } from '@/components/dashboard/StatBento'
import { VinylFeature } from '@/components/dashboard/VinylFeature'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { ScrollProgress } from '@/components/dashboard/fx/ScrollProgress'
import { SectionHeading } from '@/components/dashboard/fx/SectionHeading'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'

const SECTION = 'mx-auto w-full max-w-7xl px-6 py-20 sm:py-28'

function RowSkeletons({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-xl" />
      ))}
    </div>
  )
}

export function Dashboard() {
  const { data, activity, connected } = useDashboardChannel()
  const [discipline, setDiscipline] = useState<Discipline>('all')

  const stats = data?.[discipline]
  const { singular, plural } = workoutNouns(discipline)
  const filteredActivity = useMemo(
    () => (discipline === 'all' ? activity : activity.filter((a) => a.fitness_discipline === discipline)),
    [activity, discipline],
  )

  return (
    // reducedMotion="user": every transform animation below is skipped for
    // people who've asked their OS for less motion.
    <MotionConfig reducedMotion="user">
      <ScrollProgress />
      <div className="overflow-x-clip">
        <Hero connected={connected} stats={data?.all} />

        <DisciplineDock value={discipline} onChange={setDiscipline} />

        <section className={SECTION}>
          <SectionHeading
            index="01"
            title="On"
            accent="repeat."
            blurb={`The track that shows up in more ${plural} than any other -- and the artist who refuses to stay in one genre.`}
          />
          {stats ? (
            <VinylFeature song={stats.most_overplayed_song} artist={stats.most_versatile_artist} discipline={discipline} />
          ) : (
            <Skeleton className="h-96 rounded-[2rem]" />
          )}
        </section>

        <section className={SECTION}>
          <SectionHeading
            index="02"
            title="The catalog,"
            accent="by the numbers."
            blurb="Everything scraped so far, live. These tick upward while a crawl is running."
          />
          {stats ? (
            <StatBento stats={stats} discipline={discipline} />
          ) : (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-44 rounded-3xl" />
              ))}
            </div>
          )}
        </section>

        <section className={SECTION}>
          <SectionHeading
            index="03"
            title="Who's on"
            accent="rotation."
            blurb={`The artists that appear in the most ${plural}, and the instructors who teach the most of them.`}
          />
          <div className="grid grid-cols-1 gap-x-16 gap-y-16 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Most-played artists
              </h3>
              {stats ? (
                <RankedList
                  unit={plural}
                  entries={stats.top_artists.map((a) => ({
                    id: a.id,
                    name: a.name,
                    image_url: a.image_url,
                    count: a.play_count,
                  }))}
                />
              ) : (
                <RowSkeletons rows={10} />
              )}
            </div>
            <div>
              <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                Most active instructors
              </h3>
              {stats ? (
                <RankedList
                  unit={plural}
                  linkTo={(entry) => `/instructors/${entry.id}`}
                  entries={stats.top_instructors.map((i) => ({
                    id: i.id,
                    name: i.name,
                    image_url: i.image_url,
                    count: i.ride_count,
                  }))}
                />
              ) : (
                <RowSkeletons rows={10} />
              )}
            </div>
          </div>
        </section>

        <section className={SECTION}>
          <SectionHeading
            index="04"
            title="The"
            accent="sound."
            blurb="What Peloton's own song tags say the catalog leans on -- and the shape of the workouts themselves."
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Reveal>
              <SpotlightCard className="h-full p-6 sm:p-8">
                <h3 className="mb-6 font-display text-2xl font-semibold tracking-tight">Genre mix</h3>
                {stats ? <SoundMix data={stats.genres} unitLabel="songs" /> : <Skeleton className="h-64" />}
              </SpotlightCard>
            </Reveal>
            <Reveal delay={0.1}>
              <SpotlightCard className="h-full p-6 sm:p-8">
                <h3 className="mb-6 font-display text-2xl font-semibold tracking-tight">Popular {singular} formats</h3>
                {stats ? <SoundMix data={stats.ride_types} unitLabel={plural} /> : <Skeleton className="h-64" />}
              </SpotlightCard>
            </Reveal>
          </div>
        </section>

        <section className={SECTION}>
          <SectionHeading
            index="05"
            title="Happening"
            accent="right now."
            blurb={`Fresh playlists as the scraper finishes syncing them -- pushed over WebSockets the instant a ${singular} lands.`}
          />
          <Reveal>
            <SpotlightCard className="p-6 sm:p-10">
              <LiveFeed entries={filteredActivity} discipline={discipline} />
            </SpotlightCard>
          </Reveal>
        </section>

        <footer className="overflow-hidden pb-16 pt-10 text-center">
          <Reveal y={60}>
            <p className="select-none bg-gradient-to-b from-foreground/[0.14] to-transparent bg-clip-text font-display text-transparent text-[clamp(2.5rem,12vw,11rem)] font-semibold uppercase leading-[0.85] tracking-tighter">
              Playlist
              <br />
              Intelligence
            </p>
          </Reveal>
        </footer>
      </div>
    </MotionConfig>
  )
}
